import { useState } from "react";

import { useDispatch } from "react-redux";
import { activeQuotePageMounted } from "~/features/cart/activeQuote/redux/actions";
import Spinner from "~/common/components/Spinner/Spinner";
import ActiveQuoteEdit from "~/features/cart/activeQuote/components/ActiveQuoteEdit/ActiveQuoteEdit";
import { Mode } from "~/features/cart/cart/models/QuoteData";
import ActiveQuoteReview from "~/features/cart/activeQuote/components/ActiveQuoteReview/ActiveQuoteReview";
import { useTranslation } from "react-i18next";
import { useOnMount } from "~/common/hooks/useOnMount";
import { LinkButton } from "~/common/components/Link";
import { useActiveQuoteViewModel } from "~/features/cart/activeQuote/hooks/useActiveQuoteViewModel";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { Box, styled } from "~/common/mui";
import {
  cartProductTableStyles,
  activeQuoteStyles,
} from "~/features/cart/activeQuote/activeQuoteProductTableStyles";
import EditIcon from "@mui/icons-material/Edit";
import type { ActiveQuoteViewModel } from "~/features/cart/activeQuote/hooks/useActiveQuoteViewModel";
import { ActiveQuotePageModeContext } from "./ActiveQuotePageModeContext";

const ActiveQuoteStyleScope = styled("div")(({ theme }) => ({
  ...cartProductTableStyles(theme),
  ...activeQuoteStyles(theme),
}));

interface ActiveQuoteRootPageViewProps {
  viewModel: ActiveQuoteViewModel;
  initialPageMode?: Mode;
}

export const ActiveQuoteRootPageView = ({ viewModel, initialPageMode = Mode.EDIT }: ActiveQuoteRootPageViewProps) => {
  const { t } = useTranslation();
  const [pageMode, setPageMode] = useState<Mode>(initialPageMode);

  if (viewModel.isLoading) {
    return <Spinner />;
  }

  const accNo = viewModel.accountNumber;
  const context = !accNo ? "noAccNo" : undefined;

  const editButton = pageMode === Mode.REVIEW && (
    <LinkButton
      onClick={() => setPageMode(Mode.EDIT)}
      iconBefore={<EditIcon  />}
      style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}
    >
      {t("activeQuotePage.backToQuote")}
    </LinkButton>
  );

  return (
    <ActiveQuotePageModeContext.Provider value={setPageMode}>
      <ActiveQuoteStyleScope>
        <Box sx={{ position: "relative" }}>
          <PageTitle>{t("activeQuotePage.title", { context, accNo })}</PageTitle>
          {editButton}
        </Box>

        {pageMode === Mode.EDIT && <ActiveQuoteEdit viewModel={viewModel} />}
        {pageMode === Mode.REVIEW && <ActiveQuoteReview viewModel={viewModel} />}
      </ActiveQuoteStyleScope>
    </ActiveQuotePageModeContext.Provider>
  );
};

const ActiveQuoteRootPage = () => {
  const dispatch = useDispatch();
  const viewModel = useActiveQuoteViewModel();

  useOnMount(() => {
    dispatch(activeQuotePageMounted());
  });

  return <ActiveQuoteRootPageView viewModel={viewModel} />;
};

export default ActiveQuoteRootPage;
